import { useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Skeleton,
  Alert,
  alpha,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import axiosInstance from '../../../lib/axios';
import type { InventoryMovement, InventoryMovementType } from '../../../types';
import { ColorModeContext } from '../../../contexts/ColorModeContext';
import { palette, typography, theme } from '../../../theme/colors';

interface ProductStockHistorySectionProps {
  productId: string;
}

const movementTypeLabels: Record<InventoryMovementType, string> = {
  sale: 'SALE',
  sale_return: 'SALE RETURN',
  reservation: 'RESERVATION',
  reservation_cancel: 'RSV CANCEL',
  reservation_complete: 'RSV COMPLETE',
  transfer_out: 'TRANSFER OUT',
  transfer_in: 'TRANSFER IN',
  transfer_allocate: 'ALLOCATE',
  transfer_deallocate: 'DEALLOCATE',
  adjustment_add: 'ADD',
  adjustment_remove: 'REMOVE',
  restock: 'RESTOCK',
  loss: 'LOSS',
  count: 'COUNT',
  initial: 'INITIAL',
  delete: 'DELETED',
};

const getMovementTypeColor = (type: InventoryMovementType): string => {
  const positiveTypes: InventoryMovementType[] = ['sale_return', 'restock', 'transfer_in', 'adjustment_add', 'initial'];
  const negativeTypes: InventoryMovementType[] = ['sale', 'transfer_out', 'adjustment_remove', 'loss', 'reservation_complete', 'delete'];

  if (positiveTypes.includes(type)) return palette.success;
  if (negativeTypes.includes(type)) return palette.error;
  return palette.warning;
};

const fetchProductMovements = async (productId: string): Promise<InventoryMovement[]> => {
  const response = await axiosInstance.get(`/inventory/product/${productId}/movements`, {
    params: { limit: 10 }
  });
  return Array.isArray(response.data) ? response.data : response.data.movements || response.data.data || [];
};

export const ProductStockHistorySection = ({ productId }: ProductStockHistorySectionProps) => {
  const { mode } = useContext(ColorModeContext);

  const { data: movements = [], isLoading, error } = useQuery({
    queryKey: ['productMovements', productId],
    queryFn: () => fetchProductMovements(productId),
    enabled: !!productId,
  });

  const colors = mode === 'dark' ? {
    bg: palette.background,
    surface: palette.surface,
    surfaceElevated: palette.surfaceElevated,
    border: palette.border,
    borderLight: palette.borderLight,
    textPrimary: palette.textPrimary,
    textSecondary: palette.textSecondary,
    textMuted: palette.textMuted,
  } : {
    bg: palette.light.background,
    surface: palette.light.surface,
    surfaceElevated: palette.light.surfaceElevated,
    border: palette.light.border,
    borderLight: palette.light.borderLight,
    textPrimary: palette.light.textPrimary,
    textSecondary: palette.light.textSecondary,
    textMuted: palette.light.textMuted,
  };

  return (
    <Card
      sx={{
        backgroundColor: colors.surface,
        border: `1px solid ${colors.border}`,
        borderRadius: theme.radius.md,
      }}
    >
      <CardContent>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon sx={{ color: palette.primary, fontSize: 28 }} />
            <Typography
              variant="h6"
              sx={{
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.bold,
                color: colors.textPrimary,
                textTransform: 'uppercase',
              }}
            >
              Recent Transactions
            </Typography>
          </Box>

          {movements.length > 0 && (
            <Chip
              label={`Last ${movements.length} Transactions`}
              size="small"
              sx={{
                fontFamily: typography.fontFamily.mono,
                fontWeight: typography.fontWeight.bold,
                backgroundColor: alpha(palette.primary, 0.1),
                color: palette.primary,
                border: `1px solid ${palette.primary}`,
              }}
            />
          )}
        </Box>

        {/* Loading State */}
        {isLoading && (
          <Box>
            <Skeleton variant="rectangular" height={200} sx={{ borderRadius: theme.radius.sm }} />
          </Box>
        )}

        {/* Error State */}
        {error && (
          <Alert severity="error">
            Failed to load transaction history. Please try again.
          </Alert>
        )}

        {/* Movements Table */}
        {!isLoading && !error && (
          <>
            {movements.length === 0 ? (
              <Box sx={{ p: 6, textAlign: 'center' }}>
                <HistoryIcon sx={{ fontSize: 60, color: colors.textMuted, mb: 2 }} />
                <Typography
                  variant="h6"
                  sx={{
                    color: colors.textPrimary,
                    fontFamily: typography.fontFamily.display,
                    fontWeight: typography.fontWeight.bold,
                    mb: 1,
                  }}
                >
                  NO TRANSACTION HISTORY
                </Typography>
                <Typography variant="body2" sx={{ color: colors.textMuted }}>
                  No inventory movements recorded for this product yet.
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table
                  sx={{
                    '& .MuiTableCell-root': {
                      borderBottom: `1px solid ${colors.border}`,
                    },
                  }}
                >
                  <TableHead>
                    <TableRow
                      sx={{
                        backgroundColor: colors.surfaceElevated,
                        borderBottom: `2px solid ${palette.primary}`,
                      }}
                    >
                      <TableCell
                        sx={{
                          color: colors.textMuted,
                          fontFamily: typography.fontFamily.mono,
                          fontSize: typography.fontSize.xs,
                          fontWeight: typography.fontWeight.bold,
                          textTransform: 'uppercase',
                          letterSpacing: typography.letterSpacing.widest,
                          py: 2,
                        }}
                      >
                        Date
                      </TableCell>
                      <TableCell
                        sx={{
                          color: colors.textMuted,
                          fontFamily: typography.fontFamily.mono,
                          fontSize: typography.fontSize.xs,
                          fontWeight: typography.fontWeight.bold,
                          textTransform: 'uppercase',
                          letterSpacing: typography.letterSpacing.widest,
                        }}
                      >
                        Type
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          color: colors.textMuted,
                          fontFamily: typography.fontFamily.mono,
                          fontSize: typography.fontSize.xs,
                          fontWeight: typography.fontWeight.bold,
                          textTransform: 'uppercase',
                          letterSpacing: typography.letterSpacing.widest,
                        }}
                      >
                        Change
                      </TableCell>
                      <TableCell
                        sx={{
                          color: colors.textMuted,
                          fontFamily: typography.fontFamily.mono,
                          fontSize: typography.fontSize.xs,
                          fontWeight: typography.fontWeight.bold,
                          textTransform: 'uppercase',
                          letterSpacing: typography.letterSpacing.widest,
                        }}
                      >
                        Location
                      </TableCell>
                      <TableCell
                        sx={{
                          color: colors.textMuted,
                          fontFamily: typography.fontFamily.mono,
                          fontSize: typography.fontSize.xs,
                          fontWeight: typography.fontWeight.bold,
                          textTransform: 'uppercase',
                          letterSpacing: typography.letterSpacing.widest,
                        }}
                      >
                        Reason
                      </TableCell>
                      <TableCell
                        sx={{
                          color: colors.textMuted,
                          fontFamily: typography.fontFamily.mono,
                          fontSize: typography.fontSize.xs,
                          fontWeight: typography.fontWeight.bold,
                          textTransform: 'uppercase',
                          letterSpacing: typography.letterSpacing.widest,
                        }}
                      >
                        Performed By
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {movements.map((movement, index) => {
                      const movementColor = getMovementTypeColor(movement.movementType);
                      const isPositive = movement.quantityChange > 0;
                      const isNegative = movement.quantityChange < 0;

                      return (
                        <TableRow
                          key={movement.id}
                          sx={{
                            transition: theme.transitions.fast,
                            borderLeft: `4px solid ${movementColor}`,
                            '&:hover': {
                              backgroundColor: alpha(movementColor, 0.05),
                              borderLeft: `4px solid ${movementColor}`,
                            },
                          }}
                        >
                          {/* Date */}
                          <TableCell sx={{ py: 2 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: typography.fontFamily.mono,
                                fontSize: typography.fontSize.xs,
                                color: colors.textSecondary,
                                lineHeight: 1.4,
                              }}
                            >
                              {new Date(movement.createdAt).toLocaleDateString('en-US', {
                                month: 'short',
                                day: '2-digit',
                                year: 'numeric',
                              })}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                fontFamily: typography.fontFamily.mono,
                                fontSize: typography.fontSize.xs,
                                color: colors.textMuted,
                              }}
                            >
                              {new Date(movement.createdAt).toLocaleTimeString('en-US', {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </Typography>
                          </TableCell>

                          {/* Type */}
                          <TableCell>
                            <Chip
                              label={movementTypeLabels[movement.movementType]}
                              sx={{
                                backgroundColor: alpha(movementColor, 0.1),
                                color: movementColor,
                                fontFamily: typography.fontFamily.mono,
                                fontSize: typography.fontSize.xs,
                                fontWeight: typography.fontWeight.bold,
                                border: `1px solid ${movementColor}`,
                                height: 28,
                              }}
                            />
                          </TableCell>

                          {/* Change */}
                          <TableCell align="center">
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                px: 1.5,
                                py: 0.5,
                                borderRadius: theme.radius.xs,
                                backgroundColor: isPositive
                                  ? alpha(palette.success, 0.1)
                                  : isNegative
                                  ? alpha(palette.error, 0.1)
                                  : 'transparent',
                                border: `1px solid ${
                                  isPositive
                                    ? palette.success
                                    : isNegative
                                    ? palette.error
                                    : colors.border
                                }`,
                              }}
                            >
                              {isPositive ? (
                                <TrendingUpIcon sx={{ fontSize: 16, color: palette.success }} />
                              ) : isNegative ? (
                                <TrendingDownIcon sx={{ fontSize: 16, color: palette.error }} />
                              ) : null}
                              <Typography
                                sx={{
                                  fontFamily: typography.fontFamily.mono,
                                  fontSize: typography.fontSize.sm,
                                  fontWeight: typography.fontWeight.bold,
                                  color: isPositive
                                    ? palette.success
                                    : isNegative
                                    ? palette.error
                                    : colors.textPrimary,
                                }}
                              >
                                {isPositive ? `+${movement.quantityChange}` : movement.quantityChange}
                              </Typography>
                            </Box>
                          </TableCell>

                          {/* Location */}
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: typography.fontFamily.display,
                                fontWeight: typography.fontWeight.semibold,
                                color: colors.textPrimary,
                                fontSize: typography.fontSize.sm,
                              }}
                            >
                              {movement.location?.name || 'Unknown'}
                            </Typography>
                            {movement.location?.type && (
                              <Typography
                                variant="caption"
                                sx={{
                                  fontFamily: typography.fontFamily.mono,
                                  color: colors.textMuted,
                                  fontSize: typography.fontSize.xs,
                                  textTransform: 'uppercase',
                                }}
                              >
                                {movement.location.type === 'warehouse' ? 'Warehouse' : 'Store'}
                              </Typography>
                            )}
                          </TableCell>

                          {/* Reason */}
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: typography.fontFamily.body,
                                fontSize: typography.fontSize.sm,
                                color: colors.textSecondary,
                                maxWidth: 200,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {movement.reason || movement.notes || '-'}
                            </Typography>
                          </TableCell>

                          {/* Performed By */}
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: typography.fontFamily.mono,
                                fontSize: typography.fontSize.xs,
                                color: colors.textSecondary,
                              }}
                            >
                              {movement.performedBy
                                ? `${movement.performedBy.firstName || ''} ${movement.performedBy.lastName || ''}`.trim() ||
                                  movement.performedBy.email
                                : 'SYSTEM'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
